import axios from "axios";
import { removeUserData } from "./app/services/security";
import { storeUserData } from "./app/services/security";
import { getUserData } from "./app/services/security";

// Debug log to ensure environment variable is read
console.log("Environment Variable:", process.env.REACT_APP_API_BASE_URL);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

console.log("API Base URL:", API_BASE_URL);

const apiService = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Timeout
});

// Set the authorization token in headers
export const setAuthToken = (token) => {
  if (token) {
    apiService.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete apiService.defaults.headers.common["Authorization"];
  }
};

// Request interceptor: add token to every request
apiService.interceptors.request.use(
  (config) => {
    // Retrieve the stored user data from localStorage
    const userData = JSON.parse(localStorage.getItem("fitnesscoachingapp"));
    if (userData?.token) {
      config.headers.Authorization = `Bearer ${userData.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiService.interceptors.response.use(
  (response) => response, // Pass through successful responses
  async (error) => {
    const originalRequest = error.config;
    // Only try to refresh if:
    // 1. Status is 401,
    // 2. This is not already a retried request,
    // 3. And the request URL is not the refresh endpoint itself.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/api/security/refresh")
    ) {
      originalRequest._retry = true;
      const userData = getUserData();
      if (userData?.refreshToken) {
        try {
          console.log(
            "🔄 Attempting token refresh with",
            userData.refreshToken
          );
          const refreshResponse = await axios.post(
            `${API_BASE_URL}/api/security/refresh`,
            {
              refreshToken: userData.refreshToken,
            }
          );
          const newAccessToken = refreshResponse.data.accessToken;
          console.log("✅ New access token received:", newAccessToken);

          // Create an updated user data object
          const updatedUserData = { ...userData, token: newAccessToken };
          // Update storage using your helper
          storeUserData(updatedUserData);
          // Update Axios default header
          setAuthToken(newAccessToken);
          // Update the original request and retry
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          console.error("🚨 Token refresh failed:", refreshError);
          removeUserData();
          window.location.href = "/login";
        }
      } else {
        console.warn("🚨 No refresh token available");
        removeUserData();
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const API_ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/api/security/signin`,

  CLIENTS: `${API_BASE_URL}/api/admin/clients/all`,
  CLIENTDASHBOARD: `${API_BASE_URL}/api/user/clients`,
  //NEW_CLIENT: `${API_BASE_URL}/api/clients/create`,
  NEW_CLIENT: `${API_BASE_URL}/api/security/signup`,

  CLIENT_MENSURATIONS: (clientId) =>
    `${API_BASE_URL}/api/user/${clientId}/mensurations`,

  CLIENT_POIDS: (clientId) => `${API_BASE_URL}/api/user/${clientId}/poids`,
  CLIENT_ADD_POIDS: `${API_BASE_URL}/api/user/add/poids`,
  CLIENT_ADD_POIDS_BATCH: `${API_BASE_URL}/api/user/add/poids/batch`,

  CLIENT_SLEEP: (clientId) => `${API_BASE_URL}/api/user/${clientId}/sleep`,
  CLIENT_ADD_SLEEP: `${API_BASE_URL}/api/user/add/client/sleep`,

  CLIENT_ADD_MENSURATIONS: `${API_BASE_URL}/api/user/add/mensuration`,

  //NUTRITION
  CLIENT_NUTRITION: (clientId) =>
    `${API_BASE_URL}/api/user/${clientId}/nutrition`,
  COACH_UPDATE_CLIENT_INFORMATION_NUTRITON_PER_WEEK: (clientId) =>
    `${API_BASE_URL}/api/${clientId}/updateinformationperweek`,
  ADD_CLIENT_INFORMATION_NUTRITION: `${API_BASE_URL}/api/admin/add/nutrition`,
  // COACH_ADD_CLIENT_INFORMATION_NUTRITON_PER_WEEK: (clientId) => `${API_BASE_URL}/api/${clientId}/add/nutritioninformation`,
  FETCH_CLIENT_WITHOUT_NUTRITION: `${API_BASE_URL}/api/admin/clientsWithoutNutrition`,
  NUTRITION_CALCULATION: `${API_BASE_URL}/api/admin/nutrition/calculate`,
  PHYSICAL_ACTIVITY_FACTOR: `${API_BASE_URL}/api/admin/physicalActivityFactor`,

  CONTACT: `${API_BASE_URL}/api/apply`,

  PASSWORD_CHANGE: `${API_BASE_URL}/api/security/change-password`,

  CLIENT_ADD_NUTRITION_PICTURES: `${API_BASE_URL}/api/user/add/nutrition-pictures`,
  CLIENT_RETRIEVE_NUTRITION_PICTURES: (clientId) =>
    `${API_BASE_URL}/api/user/${clientId}/nutrition-pictures`,
  CLIENT_ADD_BODY_PICTURES: `${API_BASE_URL}/api/user/add/body-pictures`,
  CLIENT_RETRIEVE_BODY_PICTURES: (clientId) =>
    `${API_BASE_URL}/api/user/${clientId}/body-pictures`,

  RECETTE_GET_ALL: `${API_BASE_URL}/api/user/recette/all`,
  RECETTE_GET_ONE_SPECIFIC_RECETTE: (recetteId) =>
    `${API_BASE_URL}/api/user/recette/${recetteId}`,
  CREATION_RECETTE: `${API_BASE_URL}/api/admin/recette/create`,
  CREATION_FULL_RECETTE: `${API_BASE_URL}/api/admin/recette/createFull`,
  RECETTE_ADD_INGREDIENTS: (recetteId) =>
    `${API_BASE_URL}/api/admin/${recetteId}/ingredients`,
  RECETTE_INGREDIENT_SEARCH: `${API_BASE_URL}/api/user/ingredients/search`,
  RECETTE_ADD_STEPS: (recetteId) =>
    `${API_BASE_URL}/api/admin/${recetteId}/steps`,

  RECETTE_ADD_CULTURE: (recetteId) =>
    `${API_BASE_URL}/api/admin/${recetteId}/culture`,
  RECETTE_GET_CULTURE: `${API_BASE_URL}/api/user/culture/all`,
  RECETTE_ADD_CULTURE_OUTSIDE_RECIPE: "/api/admin/culture/add",

  RECETTE_ADD_CATEGORIES: (recetteId) =>
    `${API_BASE_URL}/api/admin/${recetteId}/categories`,
  RECETTE_GET_CATEGORIES: `${API_BASE_URL}/api/user/categories/all`,
  RECETTE_ADD_CATEGORIES_OUTSIDE_RECIPE: "/api/admin/categories/add",

  RECETTE_ADD_DIETARY_PREFERENCES: (recetteId) =>
    `${API_BASE_URL}/api/admin/${recetteId}/dietary-preferences`,
  RECETTE_GET_DIETARY_PREFERENCES: `${API_BASE_URL}/api/user/dietary-preferences/all`,
  ADD_DIETARY_PREFERENCE_OUTSIDE_RECIPE: "/api/admin/dietary-preferences/add",

  FORMULAIRE_DEBUT_COACHING: "/api/user/forms/formulairedebutcoaching/submit",

  // In your API_ENDPOINTS file:
  RECETTE_UPLOAD_PICTURE: (recetteId) =>
    `/api/admin/recipes/${recetteId}/uploadPicture`,

  //FITNESS
  //Core Exercises
  FITNESS_GET_MUSCLE: `${API_BASE_URL}/api/user/muscle-groups/all`,
  FITNESS_ADD_MUSCLE: `${API_BASE_URL}/api/admin/muscle-groups/add`,
  FITNESS_GET_EQUIPMENT: `${API_BASE_URL}/api/user/equipments/all`,
  FITNESS_ADD_EQUIPMENT: `${API_BASE_URL}/api/admin/equipments/add`,
  FITNESS_ADD_EXERCISE: `${API_BASE_URL}/api/admin/exercises/add`,
  FITNESS_GET_ALL_EXERCISES: `${API_BASE_URL}/api/user/exercises/all`,
  FITNESS_GET_ALL_TAGS: `${API_BASE_URL}/api/user/tags/all`,
  FITNESS_GET_ALL_EXERCISES_INTENSITY: `${API_BASE_URL}/api/user/exercise-intensity`,
  FITNESS_GET_ALL_BODY_PARTS: `${API_BASE_URL}/api/user/bodyparts/all`,
  FITNESS_GET_ALL_MAIN_FAMILIES: `${API_BASE_URL}/api/user/mainfamily/all`,
  FITNESS_GET_INFO_FOR_EXERCISE_CREATION: `${API_BASE_URL}/api/user/exercises/getalllibrary`,

  DOWNLOAD_REPORTS: (clientId) =>
    `${API_BASE_URL}/api/user/download-reports/${clientId}`,
};

export default apiService;
